// MODEL TUNNELS, the Models page's own tab (owner 2026-10-28: "a setting that tunnels
// source file from platform A, account B to platform B, account C, example, download NWD
// from SpaceX hub ACC, NWD path, then automatically start uploading it to Procore,
// DP-Electric, NWD path"; owner 2026-11-04: "add a tab for available tunnels with each
// tunnel has a manual trigger and a sync toggle to automate with a worker scheduler
// check"). It was a dialog behind a button until then.
//
// Each tunnel is a FROM folder (or picked files) on one Procore / ACC account and a TO
// folder on another, and the model types it carries. Each row carries its own two
// switches and its own button:
//
//   On            the tunnel works at all - off moves nothing, by hand or otherwise
//   Keep synced   the worker scheduler checks the FROM side by itself, every N minutes,
//                 and a newer version number there (or a newer update stamp when the
//                 provider gives no number) is downloaded and then uploaded to the TO side
//   Check now     the same check, this second, for whoever does not want to wait
//
// "Send the files already there" is the way out of the commonest surprise: a new tunnel
// starts with "only new versions" ticked, so the files that were in the FROM folder
// before it was made are noted and left alone - and the Models list stays empty, which
// reads as a broken tunnel. That button carries them through once.
//
// Folders are chosen with the same FolderPicker the Procore / ACC folders panel uses,
// browsing as the chosen account. A FROM folder reads only itself unless "include
// subfolders" is ticked; FROM files are watched one by one. A TO file takes every version
// as its own next version (one FROM file only).
import React, { useCallback, useEffect, useState } from "react";
import { Button, Table, Tag, Switch, Select, Input, Checkbox, Popconfirm, Space, Spin, Tooltip, Alert, Radio } from "antd";
import {
  ArrowRightOutlined, PlusOutlined, ReloadOutlined, DeleteOutlined, EditOutlined, FolderOpenOutlined, FileOutlined, CloseOutlined,
  CloudDownloadOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import addNotification from "helpers/notify";
import FolderPicker from "components/Integrations/FolderPicker";
import { AutodeskLogo, ProcoreLogo } from "components/Integrations/ProviderLogos";
import { getProjectFolderPaths } from "components/Integrations/folderPathsApi";
import { listModelTunnels, addModelTunnel, saveModelTunnel, removeModelTunnel, runModelTunnel } from "api/projectModels";

const NAME = { acc: "ACC", procore: "Procore" };
const EXTS = ["rvt", "nwc", "nwd", "nwf"];
// what "keep synced" may be set to; the server holds the same floor (5 minutes)
const EVERY = [5, 10, 15, 30, 60, 120, 360, 720, 1440];
const everyLabel = (m) => (m >= 60 ? `Every ${m / 60} hour${m === 60 ? "" : "s"}` : `Every ${m} minutes`);
const accountKey = (provider, connection) => `${provider}:${connection || ""}`;
const emptySide = () => ({ account: "", accountId: "", containerId: "", folderId: "", path: "", kind: "folder", files: [], subfolders: false });
const isFiles = (s) => s.kind === "files";
const when = (d) => (d ? moment(d).format("YYYY-MM-DD HH:mm") : "");

function Side({ s, label }) {
  const Logo = s.provider === "procore" ? ProcoreLogo : AutodeskLogo;
  return (
    <span className="models-source">
      <Logo connected size={16} />
      <span>
        {label ? <small className="d-block text-muted tunnel-side-label">{label}</small> : null}
        <b>{s.platform}</b>
        {s.account ? <span> · {s.account}</span> : null}
        {s.kind === "files" ? (
          (s.files || []).map((f) => <small key={f.fileId} className="d-block text-muted models-path" title={f.path}><FileOutlined /> {f.path}</small>)
        ) : (
          <small className="d-block text-muted models-path" title={s.path}>
            <FolderOpenOutlined /> {s.path}{s.subfolders ? " (and its subfolders)" : ""}
          </small>
        )}
      </span>
    </span>
  );
}

function lastRunText(r) {
  if (!r || !r.at) return "Not checked yet";
  const parts = [`${r.found || 0} found`];
  if (r.downloading) parts.push(`${r.downloading} downloading`);
  if (r.uploading) parts.push(`${r.uploading} uploading`);
  if (r.baseline) parts.push(`${r.baseline} left alone (there before the tunnel)`);
  return `${moment(r.at).fromNow()}: ${parts.join(", ")}`;
}

// a tunnel that found files and moved none of them because they were there first: the
// commonest reason the Models list shows nothing after a tunnel is added
const heldBack = (t) => !!(t.lastRun && (t.lastRun.baseline || (t.onlyNew && t.lastRun.found && !t.lastRun.downloading && !t.lastRun.uploading)));

export default function ModelTunnelsTab({ projectId, active = true, onRan, onCounts }) {
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  // the tunnel being added or changed: { id?, name, exts, from, to }
  const [form, setForm] = useState(null);
  // which side's folder is being chosen: "from" | "to"
  const [picking, setPicking] = useState("");

  const load = useCallback(() => {
    setError("");
    return listModelTunnels(projectId).then(setData).catch((e) => setError(e.message));
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    setData(null);
    setForm(null);
    setPicking("");
    load();
    getProjectFolderPaths(projectId)
      .then((r) => setAccounts((r.accounts || []).filter((a) => a.id)))
      .catch(() => setAccounts([]));
  }, [projectId, load]);

  // back on this tab: the last checks may be minutes old, so read them again
  useEffect(() => { if (active && projectId) load(); }, [active, projectId, load]);

  // the tab's badge: how many tunnels the project has and how many keep themselves synced
  useEffect(() => {
    if (!onCounts || !data) return;
    const list = data.tunnels || [];
    onCounts({ total: list.length, on: list.filter((t) => t.enabled).length, auto: list.filter((t) => t.enabled && t.auto).length });
  }, [data, onCounts]);

  const canEdit = data && data.canEdit ? data.canEdit : {};
  const accountOf = (key) => accounts.find((a) => accountKey(a.provider, a.connection) === key);
  const accountOptions = accounts
    .filter((a) => canEdit[a.provider])
    .map((a) => ({
      value: accountKey(a.provider, a.connection),
      label: `${NAME[a.provider]} - ${a.label}${a.connected ? "" : " (not connected)"}`,
      disabled: !a.connected,
    }));

  const act = async (key, fn, done) => {
    setBusy(key);
    try {
      const r = await fn();
      if (r && r.tunnels) setData((d) => ({ ...d, tunnels: r.tunnels }));
      if (done) done(r);
    } catch (e) {
      addNotification(e.message, "error");
    } finally {
      setBusy("");
    }
  };

  // "Check now" and "Send the files already there": the same run, one of them past the
  // baseline. What it queues shows on the Transfers tab, so the Models list is reloaded.
  const check = (t, includeExisting) =>
    act(`run:${t.id}`, () => runModelTunnel(projectId, t.id, { includeExisting }), (r) => {
      const ran = (r && r.ran) || {};
      if (ran.error) addNotification(ran.error, "error");
      else if (!ran.downloading && !ran.uploading) {
        addNotification(
          `${ran.found || 0} file${ran.found === 1 ? "" : "s"} in the source`
            + (ran.baseline ? `, ${ran.baseline} left alone because they were there before the tunnel - use "Send the files already there" to carry them through` : ", nothing new to move"),
          "warning",
        );
      } else addNotification(`${ran.found || 0} found, ${ran.downloading || 0} downloading, ${ran.uploading || 0} uploading`, "success");
      if (onRan && (ran.downloading || ran.uploading)) onRan();
    });

  const sideBody = (s) => {
    const a = accountOf(s.account);
    return {
      provider: a.provider, connection: a.connection, accountId: s.accountId, containerId: s.containerId,
      kind: s.kind, subfolders: !isFiles(s) && !!s.subfolders,
      folderId: isFiles(s) ? "" : s.folderId, path: isFiles(s) ? "" : s.path,
      files: isFiles(s) ? s.files : [],
    };
  };
  const sideChosen = (s) => (isFiles(s) ? s.files.length > 0 : !!s.folderId);

  const save = () => {
    if (!sideChosen(form.from) || !sideChosen(form.to)) {
      addNotification("Choose both sides: what the tunnel downloads and where it uploads to", "warning");
      return;
    }
    if (isFiles(form.to) && !(isFiles(form.from) && form.from.files.length === 1)) {
      addNotification("Uploading into one file needs exactly one file on the download side", "warning");
      return;
    }
    const body = {
      name: form.name, exts: form.exts, onlyNew: !!form.onlyNew, auto: form.auto !== false, everyMinutes: form.everyMinutes || 15,
      from: sideBody(form.from), to: sideBody(form.to),
    };
    act("save", () => (form.id ? saveModelTunnel(projectId, form.id, body) : addModelTunnel(projectId, body)), () => {
      addNotification(form.id ? "Tunnel saved" : "Tunnel added - checking its folder now", "success");
      setForm(null);
      // the first check runs on the server; read its result in a moment
      if (!form.id) setTimeout(() => load(), 8000);
    });
  };

  const sideForm = (x) => ({
    account: accountKey(x.provider, x.connection), accountId: x.accountId, containerId: x.containerId, folderId: x.folderId, path: x.path,
    kind: x.kind || "folder", files: x.files || [], subfolders: !!x.subfolders,
  });
  const edit = (t) => setForm({
    id: t.id, name: t.name, exts: t.exts || [], onlyNew: !!t.onlyNew, auto: t.auto !== false, everyMinutes: t.everyMinutes || 15,
    from: sideForm(t.from), to: sideForm(t.to),
  });

  const columns = [
    {
      title: "Tunnel",
      key: "name",
      width: 170,
      sorter: (a, b) => (a.name || a.title).localeCompare(b.name || b.title),
      render: (x, t) => (
        <div>
          <b>{t.name || t.title}</b>
          <small className="d-block text-muted">Added {when(t.createdAt)}</small>
          {(t.exts || []).length ? t.exts.map((e) => <Tag key={e}>{e.toUpperCase()}</Tag>) : <Tag>All model files</Tag>}
        </div>
      ),
    },
    {
      title: "From path",
      key: "from",
      sorter: (a, b) => String(a.from.path || "").localeCompare(String(b.from.path || "")),
      render: (x, t) => <Side s={t.from} label="From" />,
    },
    { title: "", key: "arrow", width: 24, render: () => <ArrowRightOutlined /> },
    {
      title: "To path",
      key: "to",
      sorter: (a, b) => String(a.to.path || "").localeCompare(String(b.to.path || "")),
      render: (x, t) => <Side s={t.to} label="To" />,
    },
    {
      title: "Last check",
      key: "lastRun",
      width: 210,
      sorter: (a, b) => new Date((a.lastRun && a.lastRun.at) || 0) - new Date((b.lastRun && b.lastRun.at) || 0),
      render: (x, t) => (
        <small className={t.lastRun && t.lastRun.error ? "text-danger" : "text-muted"}>
          {lastRunText(t.lastRun)}
          {t.lastRun && t.lastRun.how ? <div>{t.lastRun.how === "manual" ? "Checked by hand" : "Automatic check"}</div> : null}
          {t.lastRun && t.lastRun.error ? <div>{t.lastRun.error}</div> : null}
          {heldBack(t) ? <div data-testid={`tunnel-held-${t.id}`}>Files that were there before the tunnel are left alone.</div> : null}
        </small>
      ),
    },
    {
      title: "Keep synced",
      key: "auto",
      width: 150,
      render: (x, t) => {
        const may = canEdit[t.from.provider] && canEdit[t.to.provider];
        return (
          <Space direction="vertical" size={2}>
            <Tooltip title={t.auto
              ? `On: the worker checks this tunnel's source by itself ${everyLabel(t.everyMinutes).toLowerCase()} and carries any newer version through. Off: only "Check now" moves it.`
              : "Off: nothing is checked on its own - use Check now. Turn this on to have the worker watch the source folder for new versions and upload dates."}>
              <Switch
                size="small"
                checked={!!t.auto}
                loading={busy === `auto:${t.id}`}
                disabled={!may || !t.enabled}
                data-testid={`tunnel-auto-${t.id}`}
                onChange={(v) => act(`auto:${t.id}`, () => saveModelTunnel(projectId, t.id, { auto: v }))}
              />
            </Tooltip>
            <Tooltip title="How often the worker checks the source folder for a newer version or upload date">
              <Select
                size="small"
                style={{ width: 130 }}
                value={t.everyMinutes || 15}
                disabled={!may || !t.enabled || !t.auto}
                data-testid={`tunnel-every-${t.id}`}
                options={EVERY.map((m) => ({ value: m, label: everyLabel(m) }))}
                onChange={(v) => act(`every:${t.id}`, () => saveModelTunnel(projectId, t.id, { everyMinutes: v }))}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "On",
      key: "enabled",
      width: 56,
      render: (x, t) => (
        <Tooltip title={t.enabled
          ? "On. Turn it off to stop this tunnel moving anything - its folders and settings are kept."
          : "Off: this tunnel moves nothing, by hand or on its own. Its folders and settings are kept."}>
          <Switch
            size="small"
            checked={t.enabled}
            loading={busy === `on:${t.id}`}
            disabled={!canEdit[t.from.provider] || !canEdit[t.to.provider]}
            data-testid={`tunnel-on-${t.id}`}
            onChange={(v) => act(`on:${t.id}`, () => saveModelTunnel(projectId, t.id, { enabled: v }))}
          />
        </Tooltip>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 150,
      render: (x, t) => {
        const may = canEdit[t.from.provider] && canEdit[t.to.provider];
        return (
          <Space size={4} wrap>
            <Tooltip title={t.enabled ? "Check the source now: any new file or version there is downloaded and then uploaded to the To folder" : "Turn the tunnel on first"}>
              <Button size="small" icon={<ReloadOutlined />} loading={busy === `run:${t.id}`} disabled={!may || !t.enabled}
                data-testid={`tunnel-run-${t.id}`} onClick={() => check(t, false)}>
                Check now
              </Button>
            </Tooltip>
            <Tooltip title="Carry everything in the source through now, including the files that were already there when the tunnel was made - use this when the Models list stays empty after adding a tunnel">
              <Popconfirm
                title="Send the files already there?"
                description="Every model file in the source folder is downloaded and uploaded to the To folder, not only the new ones."
                okText="Send them"
                disabled={!may || !t.enabled}
                onConfirm={() => check(t, true)}
              >
                <Button size="small" icon={<CloudDownloadOutlined />} disabled={!may || !t.enabled} data-testid={`tunnel-run-existing-${t.id}`}>
                  Send existing
                </Button>
              </Popconfirm>
            </Tooltip>
            <Tooltip title="Change this tunnel's folders, file types and settings">
              <Button size="small" icon={<EditOutlined />} disabled={!may} data-testid={`tunnel-edit-${t.id}`} onClick={() => edit(t)} />
            </Tooltip>
            <Tooltip title="Remove this tunnel. Nothing changes on either platform.">
              <Popconfirm title="Remove this tunnel? Nothing changes on either platform." okText="Remove" onConfirm={() => act(`rm:${t.id}`, () => removeModelTunnel(projectId, t.id))} disabled={!may}>
                <Button size="small" danger icon={<DeleteOutlined />} loading={busy === `rm:${t.id}`} disabled={!may} data-testid={`tunnel-remove-${t.id}`} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const sideEditor = (which, label) => {
    const s = form[which];
    const a = accountOf(s.account);
    const set = (patch) => setForm((f) => ({ ...f, [which]: { ...f[which], ...patch } }));
    const removeFile = (id) => set({ files: s.files.filter((x) => x.fileId !== id) });
    return (
      <div className="mb-3 tunnel-side">
        <div><b>{label}</b></div>
        <Space wrap>
          <Tooltip title={which === "from" ? "The platform and account the files are downloaded from" : "The platform and account the files are uploaded to"}>
            <Select
              style={{ minWidth: 260 }}
              placeholder="Platform and account"
              value={s.account || undefined}
              options={accountOptions}
              onChange={(v) => setForm((f) => ({ ...f, [which]: { ...emptySide(), account: v } }))}
              data-testid={`tunnel-${which}-account`}
            />
          </Tooltip>
          <Tooltip title={which === "from"
            ? "A folder: every model file in it (and under it, if you tick that). Specific files: only the ones you pick."
            : "A folder: each file is uploaded into it under its own name. One file: every version is uploaded as the next version of that one file."}>
            <Radio.Group
              value={s.kind}
              optionType="button"
              size="small"
              onChange={(e) => set({ kind: e.target.value })}
              options={[
                { value: "folder", label: "A folder" },
                { value: "files", label: which === "from" ? "Specific files" : "One file" },
              ]}
            />
          </Tooltip>
          {isFiles(s) ? (
            <Tooltip title={which === "from" ? "Browse the account and pick each file to watch" : "Browse the account and pick the file every version is uploaded into"}>
              <Button icon={<FileOutlined />} disabled={!a || (which === "to" && s.files.length >= 1)} onClick={() => setPicking(which)} data-testid={`tunnel-${which}-files`}>
                {which === "from" ? "Add files" : "Choose file"}
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title={`Browse the account and choose the folder to ${which === "from" ? "download from" : "upload to"}`}>
              <Button icon={<FolderOpenOutlined />} disabled={!a} onClick={() => setPicking(which)}>
                {s.folderId ? "Change folder" : "Choose folder"}
              </Button>
            </Tooltip>
          )}
        </Space>
        {!isFiles(s) && s.path ? <div className="text-muted mt-1"><small>{a ? `${NAME[a.provider]}: ` : ""}{s.path}</small></div> : null}
        {!isFiles(s) && which === "from" ? (
          <div className="mt-1">
            <Tooltip title="Off: only the files directly in the folder. On: the files in the folders under it as well.">
              <Checkbox checked={!!s.subfolders} onChange={(e) => set({ subfolders: e.target.checked })}>
                Include the folders under it
              </Checkbox>
            </Tooltip>
          </div>
        ) : null}
        {isFiles(s) && s.files.length ? (
          <ul className="tunnel-files">
            {s.files.map((f) => (
              <li key={f.fileId}>
                <FileOutlined /> <span title={f.path}>{f.path || f.name}</span>
                <Tooltip title="Take this file off the tunnel">
                  <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => removeFile(f.fileId)} />
                </Tooltip>
              </li>
            ))}
          </ul>
        ) : null}
        {isFiles(s) && which === "to" ? (
          <small className="d-block text-muted">Each version is uploaded as the next version of this file, under its name.</small>
        ) : null}
      </div>
    );
  };

  if (!projectId) return null;
  if (error) return <Alert type="error" message={error} />;
  if (!data) return <Spin />;

  if (form && picking) {
    const side = form[picking];
    const a = accountOf(side.account);
    const files = isFiles(side);
    return (
      <div className="models-tunnels" data-testid="models-tunnels-tab">
        <p>
          <b>{picking === "from" ? "Download from" : "Upload to"}: {NAME[a.provider]} - {a.label}</b>
          <br />
          <small className="text-muted">
            {files
              ? (picking === "from" ? "Open a folder and click each file to add it; Done when finished." : "Open the folder and click the file.")
              : "Open the folder, then Use this folder."}
          </small>
        </p>
        {files && picking === "from" ? (
          <div className="mb-2">
            <Tooltip title="Finish picking files and go back to the tunnel">
              <Button type="primary" size="small" onClick={() => setPicking("")}>Done ({side.files.length} picked)</Button>
            </Tooltip>
          </div>
        ) : null}
        <FolderPicker
          provider={a.provider}
          connection={a.connection}
          mode={files ? "file" : "folder"}
          onCancel={() => setPicking("")}
          onPick={(t) => {
            if (files) {
              const f = { fileId: String(t.itemId), name: t.name, folderId: t.folderId, path: t.path };
              setForm((x) => {
                const cur = x[picking];
                // one account / project per side: a file from another project starts the list again
                const same = cur.containerId === t.containerId;
                const list = picking === "to" ? [f] : [...(same ? cur.files : []).filter((y) => y.fileId !== f.fileId), f];
                return { ...x, [picking]: { ...cur, accountId: t.accountId, containerId: t.containerId, files: list } };
              });
              if (picking === "to") setPicking("");
              return;
            }
            setForm((x) => ({ ...x, [picking]: { ...x[picking], accountId: t.accountId, containerId: t.containerId, folderId: t.folderId, path: t.path } }));
            setPicking("");
          }}
        />
      </div>
    );
  }

  if (form) {
    return (
      <div className="models-tunnels" data-testid="tunnel-form">
        <div className="mb-2">
          <div><b>Name</b> <small className="text-muted">(optional)</small></div>
          <Tooltip title="What this tunnel is called on this tab and in the Models list's Model tunnel column">
            <Input value={form.name} placeholder="SpaceX NWD to DP-Electric" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} maxLength={120} />
          </Tooltip>
        </div>
        {sideEditor("from", "Download from")}
        {sideEditor("to", "Upload to")}
        <div className="mb-2">
          <div><b>File types</b> <small className="text-muted">(none ticked = every model file)</small></div>
          <Checkbox.Group options={EXTS.map((e) => ({ value: e, label: `.${e}` }))} value={form.exts} onChange={(v) => setForm((f) => ({ ...f, exts: v }))} />
        </div>
        <div className="mb-2">
          <Tooltip title="On: the worker checks the source by itself and carries any newer version through. Off: only Check now moves this tunnel.">
            <Checkbox checked={form.auto !== false} onChange={(e) => setForm((f) => ({ ...f, auto: e.target.checked }))} data-testid="tunnel-form-auto">
              Keep synced automatically
            </Checkbox>
          </Tooltip>
          <Tooltip title="How often the worker checks the source folder for a new version number or upload date">
            <Select
              size="small"
              className="ms-2"
              style={{ width: 150 }}
              value={form.everyMinutes || 15}
              disabled={form.auto === false}
              data-testid="tunnel-form-every"
              options={EVERY.map((m) => ({ value: m, label: everyLabel(m) }))}
              onChange={(v) => setForm((f) => ({ ...f, everyMinutes: v }))}
            />
          </Tooltip>
        </div>
        <div className="mb-3">
          <Tooltip title="Ticked: the files already in the source when the tunnel is made are noted and left alone. Not ticked: they all go through on the first check.">
            <Checkbox checked={!!form.onlyNew} onChange={(e) => setForm((f) => ({ ...f, onlyNew: e.target.checked }))} data-testid="tunnel-only-new">
              Only new versions from now on
            </Checkbox>
          </Tooltip>
          <small className="d-block text-muted">
            Ticked: what is already there at the first check is left alone; only files and versions that appear after it go through.
            Not ticked: everything there goes through on the first check.
          </small>
        </div>
        <Space>
          <Tooltip title={form.id ? "Save these settings" : "Add the tunnel and check its source folder now"}>
            <Button type="primary" loading={busy === "save"} onClick={save} data-testid="tunnel-save">{form.id ? "Save" : "Add tunnel"}</Button>
          </Tooltip>
          <Tooltip title="Leave this tunnel as it was">
            <Button onClick={() => setForm(null)}>Cancel</Button>
          </Tooltip>
        </Space>
      </div>
    );
  }

  const list = data.tunnels || [];
  return (
    <div className="models-tunnels" data-testid="models-tunnels-tab">
      <div className="models-toolbar">
        <small className="text-muted">
          A tunnel downloads new model files and new versions from a folder or from specific files on one account, then uploads each to a
          folder (or into one file) on another account as the next version. What it moves shows on the Transfers tab, where it can be
          paused or cancelled, and on the Files tab under its own column.
        </small>
        <span className="models-spacer" />
        <Tooltip title="Read the tunnels and their last checks again">
          <Button icon={<ReloadOutlined />} onClick={load} data-testid="tunnels-refresh">Refresh</Button>
        </Tooltip>
      </div>
      {list.some((t) => t.enabled && !t.auto) ? (
        <Alert
          type="info"
          showIcon
          className="mb-2"
          icon={<ClockCircleOutlined />}
          message={`${list.filter((t) => t.enabled && !t.auto).length} tunnel(s) are not kept synced`}
          description='Nothing is checked for them on its own - turn "Keep synced" on, or use "Check now" each time.'
        />
      ) : null}
      {list.some(heldBack) ? (
        <Alert
          type="warning"
          showIcon
          className="mb-2"
          data-testid="tunnels-held-banner"
          message="A tunnel left the files that were already there alone"
          description={'These tunnels only carry versions that appear after they were made ("Only new versions"), so nothing shows on the Files tab yet. "Send existing" carries what is in the source through once.'}
        />
      ) : null}
      <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={list}
        scroll={{ x: 1200 }}
        locale={{ emptyText: "No tunnels on this project yet" }} />
      {canEdit.procore || canEdit.acc ? (
        <Tooltip title="Set up a new tunnel: a folder or files on one account, a folder on another">
          <Button className="mt-2" icon={<PlusOutlined />} data-testid="tunnel-add"
            onClick={() => setForm({ name: "", exts: [], onlyNew: true, auto: true, everyMinutes: 15, from: emptySide(), to: emptySide() })}>
            Add tunnel
          </Button>
        </Tooltip>
      ) : (
        <p className="text-muted mt-2">Only people who manage the company&apos;s Procore and ACC connections can add tunnels.</p>
      )}
    </div>
  );
}
